import { SamlMetadataSourceKind, SamlNameIdFormat } from '@today/types'
import { Field, SelectField, Upload } from '@today/ui-components'
import { HelpText, Label, Radio, RadioGroup, Switch, Textarea } from '@today/ui-primitives'
import { useId } from 'react'
import type { SamlDraft } from './use-provider-form-state'

/**
 * Props for {@link SamlFields}.
 *
 * @property draft - The SAML inputs as typed.
 * @property onChange - Reports one changed input.
 * @property isEdit - An edit: the stored document and certificate are never returned, so they must be re-supplied.
 * @property isDisabled - Disables every input.
 */
export interface SamlFieldsProps {
	draft: SamlDraft
	onChange: <K extends keyof SamlDraft>(key: K, value: SamlDraft[K]) => void
	isEdit?: boolean
	isDisabled?: boolean
}

const NAME_ID_OPTIONS = [
	{ id: SamlNameIdFormat.EMAIL_ADDRESS, text: 'Email address' },
	{ id: SamlNameIdFormat.UNSPECIFIED, text: 'Unspecified' },
	{ id: SamlNameIdFormat.PERSISTENT, text: 'Persistent' },
	{ id: SamlNameIdFormat.TRANSIENT, text: 'Transient' },
]

type Props = SamlFieldsProps

/** A labelled multi-line input. */
function TextBlock(props: { label: string; value: string; onChange: (value: string) => void; disabled?: boolean }) {
	const id = useId()
	return (
		<div className="flex flex-col gap-1.5">
			<Label htmlFor={id}>{props.label}</Label>
			<Textarea
				id={id}
				value={props.value}
				disabled={props.disabled}
				onChange={(event) => props.onChange(event.target.value)}
			/>
		</div>
	)
}

/** The uploaded or pasted metadata document. Reading the file is local; nothing is sent from here. */
function XmlSource({ draft, onChange, isDisabled }: Props) {
	const select = (files: File[]) => {
		const file = files[0]
		if (!file) return
		const reader = new FileReader()
		reader.onload = () => onChange('metadataXml', String(reader.result))
		reader.readAsText(file)
	}
	return (
		<div className="flex flex-col gap-3">
			<Upload label="Upload metadata XML" accept=".xml,application/xml,text/xml" files={[]} onFilesSelected={select} />
			<TextBlock
				label="Metadata XML"
				value={draft.metadataXml}
				disabled={isDisabled}
				onChange={(value) => onChange('metadataXml', value)}
			/>
		</div>
	)
}

/** Metadata values entered one by one. */
function FieldsSource({ draft, onChange, isDisabled }: Props) {
	return (
		<div className="flex flex-col gap-3">
			<Field
				label="Entity ID"
				value={draft.entityId}
				disabled={isDisabled}
				onChange={(event) => onChange('entityId', event.target.value)}
			/>
			<Field
				label="SSO URL"
				value={draft.ssoUrl}
				disabled={isDisabled}
				onChange={(event) => onChange('ssoUrl', event.target.value)}
			/>
			<Field
				label="SLO URL (optional)"
				value={draft.sloUrl}
				disabled={isDisabled}
				onChange={(event) => onChange('sloUrl', event.target.value)}
			/>
			<TextBlock
				label="Signing certificate (PEM)"
				value={draft.certificatePem}
				disabled={isDisabled}
				onChange={(value) => onChange('certificatePem', value)}
			/>
			<SelectField
				label="NameID format"
				options={NAME_ID_OPTIONS}
				value={draft.nameIdFormat}
				onValueChange={(value) => onChange('nameIdFormat', value as SamlNameIdFormat)}
			/>
		</div>
	)
}

/** The inputs of the selected metadata source. */
function SourceInputs(props: Props) {
	const { draft, onChange, isDisabled } = props
	if (draft.source === SamlMetadataSourceKind.XML) return <XmlSource {...props} />
	if (draft.source === SamlMetadataSourceKind.FIELDS) return <FieldsSource {...props} />
	return (
		<Field
			label="Metadata URL"
			placeholder="https://idp.example.com/saml/metadata"
			description="Cognito fetches the document from this address."
			value={draft.metadataUrl}
			disabled={isDisabled}
			onChange={(event) => onChange('metadataUrl', event.target.value)}
		/>
	)
}

/** Signing and attribute-mapping options, shared by every source. */
function SamlOptions({ draft, onChange, isDisabled }: Props) {
	return (
		<div className="flex flex-col gap-3">
			<Switch
				isSelected={draft.signRequests}
				isDisabled={isDisabled}
				onChange={(value) => onChange('signRequests', value)}
			>
				Sign authentication requests
			</Switch>
			<Field
				label="Email attribute"
				value={draft.email}
				disabled={isDisabled}
				onChange={(event) => onChange('email', event.target.value)}
			/>
			<Field
				label="Groups attribute (optional)"
				description="A group marker only — it never grants roles or another tenant's access."
				value={draft.groups}
				disabled={isDisabled}
				onChange={(event) => onChange('groups', event.target.value)}
			/>
		</div>
	)
}

/**
 * The SAML half of {@link ProviderForm}: a metadata-source switcher (URL,
 * uploaded XML, or fields) and the signing and mapping options. Pure: the
 * mapping guard and metadata validation are the server's.
 */
export function SamlFields(props: SamlFieldsProps) {
	const { draft, onChange, isEdit = false, isDisabled = false } = props
	return (
		<div className="flex flex-col gap-4">
			<RadioGroup
				aria-label="Metadata source"
				value={draft.source}
				isDisabled={isDisabled}
				onChange={(value) => onChange('source', value as SamlMetadataSourceKind)}
			>
				<Radio value={SamlMetadataSourceKind.URL}>From a URL</Radio>
				<Radio value={SamlMetadataSourceKind.XML}>Upload XML</Radio>
				<Radio value={SamlMetadataSourceKind.FIELDS}>Enter fields</Radio>
			</RadioGroup>
			{isEdit && draft.source !== SamlMetadataSourceKind.URL ? (
				<HelpText>
					The stored document and certificate are never shown again. Re-supply them to change the configuration.
				</HelpText>
			) : null}
			<SourceInputs {...props} isDisabled={isDisabled} />
			<SamlOptions {...props} isDisabled={isDisabled} />
		</div>
	)
}
